import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Grid, TextField, Checkbox } from "@mui/material";
import * as RoleActions from "../../redux/actions/roleAction.js";

// Hierarchical modules matching sidebar navigation
const MODULES_CONFIG = [
    { name: 'Dashboard', type: 'single' },
    { name: 'Astrologer', type: 'group', subModules: ['List Of Astrologers', 'Astrologer Enquiry', 'Withdrawal Request', 'Manage Astrologers'] },
    { name: 'New Users', type: 'single' },
    { name: 'Notifications', type: 'single' },
    { name: 'App Config', type: 'single' },
    { name: 'Utilities', type: 'single' },
    { name: 'Astro Mall', type: 'group', subModules: ['Category', 'Product', 'Order History'] },
    { name: 'Astro Puja', type: 'group', subModules: ['Puja', 'Puja Request', 'Puja Booked', 'Puja History'] },
    { name: 'Skill', type: 'single' },
    { name: 'Remedies', type: 'single' },
    { name: 'Main Expertise', type: 'single' },
    { name: 'Language', type: 'single' },
    { name: 'Banner', type: 'single' },
    { name: 'Category', type: 'single' },
    { name: 'Sub Category', type: 'single' },
    { name: 'Yatra', type: 'group', subModules: ['Yatra', 'Yatra History'] },
    { name: 'Sanchar', type: 'group', subModules: ['Podcast', 'Videoclip', 'News', 'Live'] },
    { name: 'Religious', type: 'group', subModules: ['Daily Darshan', 'Consultant Requests'] },
    { name: 'Ecommerce', type: 'group', subModules: ['Category', 'Sub Category', 'Attribute', 'Product'] },
    { name: 'TBO Bookings', type: 'group', subModules: ['TBO Settings', 'Flight Bookings', 'Hotel Bookings'] },
    { name: 'Milan Sathi', type: 'group', subModules: ['Dashboard', 'Users', 'Verifications', 'Plans', 'Transactions'] },
    { name: 'Astrologer Admin', type: 'group', subModules: ['KYC Approvals', 'Consultations', 'Payouts', 'Ratings & Reviews'] },
    { name: 'Post Banao', type: 'single' },
    { name: 'Rojgar', type: 'group', subModules: ['Dashboard', 'Job Seekers', 'Provider KYC', 'Job Moderation'] },
    { name: 'Marketplace', type: 'group', subModules: ['Dashboard', 'Vendor Management', 'Product Approvals', 'Orders', 'Commission Rules', 'Settlements'] },
    { name: 'Sanatani Manch', type: 'group', subModules: ['Dashboard', 'Service Plans', 'Purchase History', 'Users', 'Posts', 'Categories', 'Events', 'Flags & Reports'] },
    { name: 'Roles & Logs', type: 'group', subModules: ['Role Management', 'Sub Admins', 'Activity Logs'] },
    { name: 'Static Pages', type: 'group', subModules: ['About Us', 'Terms & Conditions', 'Privacy Policies', 'Refund Policies'] },
];

const ACTIONS = ['view', 'add', 'edit', 'delete'];

const AddRole = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const editData = location.state?.roleData;
    const isEdit = !!editData;

    const [roleName, setRoleName] = useState(editData?.name || '');
    const [description, setDescription] = useState(editData?.description || '');
    const [permissions, setPermissions] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});
    const [errors, setErrors] = useState({});

    // Initialize permissions from editData (handles both old flat format and new hierarchical format)
    useEffect(() => {
        const initial = {};
        MODULES_CONFIG.forEach(mod => {
            if (mod.type === 'single') {
                const key = mod.name + ':null';
                const existing = editData?.permissions?.find(p => p.module === mod.name && (!p.subModule || p.subModule === null));
                initial[key] = {
                    view: existing?.view || false,
                    add: existing?.add || false,
                    edit: existing?.edit || false,
                    delete: existing?.delete || false,
                };
            } else {
                // Check for old format: parent module without subModule (backward compat)
                const parentPerm = editData?.permissions?.find(p => p.module === mod.name && (!p.subModule || p.subModule === null));
                mod.subModules.forEach(sub => {
                    const key = mod.name + ':' + sub;
                    // New format: specific subModule permission
                    const subPerm = editData?.permissions?.find(p => p.module === mod.name && p.subModule === sub);
                    // Use sub-item perm if available, otherwise fall back to parent perm (old format)
                    const existing = subPerm || parentPerm;
                    initial[key] = {
                        view: existing?.view || false,
                        add: existing?.add || false,
                        edit: existing?.edit || false,
                        delete: existing?.delete || false,
                    };
                });
            }
        });
        setPermissions(initial);

        // Auto-expand groups that have permissions in edit mode
        if (isEdit) {
            const expanded = {};
            MODULES_CONFIG.filter(m => m.type === 'group').forEach(m => {
                const hasPerm = m.subModules.some(sub => {
                    const key = m.name + ':' + sub;
                    const p = initial[key];
                    return p?.view || p?.add || p?.edit || p?.delete;
                });
                if (hasPerm) expanded[m.name] = true;
            });
            setExpandedGroups(expanded);
        }
    }, [editData]);

    const toggleGroup = (groupName) => {
        setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    };

    const handlePermChange = (key, action) => {
        setPermissions(prev => ({
            ...prev,
            [key]: { ...prev[key], [action]: !prev[key]?.[action] }
        }));
    };

    const handleSelectAllForKey = (key) => {
        const current = permissions[key] || {};
        const allChecked = current.view && current.add && current.edit && current.delete;
        setPermissions(prev => ({
            ...prev,
            [key]: { view: !allChecked, add: !allChecked, edit: !allChecked, delete: !allChecked }
        }));
    };

    // Toggle a specific action (view/add/edit/delete) for all sub-items in a group
    const handleGroupToggle = (mod, action) => {
        const keys = mod.subModules.map(sub => mod.name + ':' + sub);
        const allChecked = keys.every(k => permissions[k]?.[action]);
        setPermissions(prev => {
            const updated = { ...prev };
            keys.forEach(k => {
                updated[k] = { ...updated[k], [action]: !allChecked };
            });
            return updated;
        });
    };

    // Toggle ALL actions for ALL sub-items in a group
    const handleGroupSelectAll = (mod) => {
        const keys = mod.subModules.map(sub => mod.name + ':' + sub);
        const allChecked = keys.every(k => {
            const p = permissions[k];
            return p?.view && p?.add && p?.edit && p?.delete;
        });
        setPermissions(prev => {
            const updated = { ...prev };
            keys.forEach(k => {
                updated[k] = { view: !allChecked, add: !allChecked, edit: !allChecked, delete: !allChecked };
            });
            return updated;
        });
    };

    const isGroupActionChecked = (mod, action) => {
        return mod.subModules.every(sub => permissions[mod.name + ':' + sub]?.[action]);
    };

    const isGroupAllChecked = (mod) => {
        return mod.subModules.every(sub => {
            const p = permissions[mod.name + ':' + sub];
            return p?.view && p?.add && p?.edit && p?.delete;
        });
    };

    const isGroupPartial = (mod, action) => {
        const checks = mod.subModules.map(sub => permissions[mod.name + ':' + sub]?.[action]);
        return checks.some(c => c) && !checks.every(c => c);
    };

    const handleSelectAllModules = () => {
        const allKeys = Object.keys(permissions);
        const allChecked = allKeys.every(k => {
            const p = permissions[k];
            return p?.view && p?.add && p?.edit && p?.delete;
        });
        const updated = {};
        allKeys.forEach(k => {
            updated[k] = { view: !allChecked, add: !allChecked, edit: !allChecked, delete: !allChecked };
        });
        setPermissions(updated);
    };

    const allGlobalChecked = Object.keys(permissions).length > 0 && Object.keys(permissions).every(k => {
        const p = permissions[k];
        return p?.view && p?.add && p?.edit && p?.delete;
    });

    const handleSubmit = () => {
        if (!roleName.trim()) {
            setErrors({ roleName: 'Role name is required' });
            return;
        }

        const permArray = [];
        MODULES_CONFIG.forEach(mod => {
            if (mod.type === 'single') {
                const key = mod.name + ':null';
                const p = permissions[key] || {};
                permArray.push({
                    module: mod.name,
                    subModule: null,
                    view: p.view || false,
                    add: p.add || false,
                    edit: p.edit || false,
                    delete: p.delete || false,
                });
            } else {
                mod.subModules.forEach(sub => {
                    const key = mod.name + ':' + sub;
                    const p = permissions[key] || {};
                    permArray.push({
                        module: mod.name,
                        subModule: sub,
                        view: p.view || false,
                        add: p.add || false,
                        edit: p.edit || false,
                        delete: p.delete || false,
                    });
                });
            }
        });

        const userId = localStorage.getItem('userId');
        const payload = {
            data: { name: roleName, description, permissions: permArray, createdBy: userId, adminName: 'Admin' },
            onComplete: () => navigate("/roles")
        };

        if (isEdit) {
            payload.id = editData._id;
            payload.data.updatedBy = userId;
            dispatch(RoleActions.updateRole(payload));
        } else {
            dispatch(RoleActions.createRole(payload));
        }
    };

    return (
        <div style={{ padding: "20px", backgroundColor: "#fff", boxShadow: '0px 0px 5px lightgrey', borderRadius: "10px" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px", fontFamily: 'Philosopher' }}>
                <div style={{ fontSize: "20px", fontWeight: "600", color: "#667284" }}>{isEdit ? 'Edit Role' : 'Add New Role'}</div>
                <div onClick={() => navigate("/roles")} style={{ fontWeight: "600", backgroundColor: "#164E62", color: "#fff", padding: "5px 10px", borderRadius: "5px", cursor: "pointer", fontSize: "14px" }}>Back to Roles</div>
            </div>

            {/* Role Name & Description */}
            <Grid container spacing={2} sx={{ marginBottom: "25px" }}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <TextField label="Role Name" variant="outlined" fullWidth value={roleName} onChange={(e) => { setRoleName(e.target.value); setErrors({}); }}
                        error={!!errors.roleName} helperText={errors.roleName} />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <TextField label="Description" variant="outlined" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
                </Grid>
            </Grid>

            {/* Permission Matrix Header */}
            <div style={{ marginBottom: "15px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: "16px", fontWeight: "600", color: "#333" }}>Permission Matrix (Sidebar Structure)</div>
                <div onClick={handleSelectAllModules} style={{ cursor: "pointer", padding: "6px 16px", backgroundColor: allGlobalChecked ? "#e57373" : "#164E62", color: "#fff", borderRadius: "5px", fontSize: "13px", fontWeight: "500" }}>
                    {allGlobalChecked ? 'Deselect All' : 'Select All'}
                </div>
            </div>

            {/* Permission Table */}
            <div style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#164E62", color: "#fff" }}>
                            <th style={{ padding: "12px 15px", textAlign: "left", minWidth: "280px" }}>Module / Sub-Module</th>
                            <th style={{ padding: "12px 15px", textAlign: "center", width: "70px" }}>View</th>
                            <th style={{ padding: "12px 15px", textAlign: "center", width: "70px" }}>Add</th>
                            <th style={{ padding: "12px 15px", textAlign: "center", width: "70px" }}>Edit</th>
                            <th style={{ padding: "12px 15px", textAlign: "center", width: "70px" }}>Delete</th>
                            <th style={{ padding: "12px 15px", textAlign: "center", width: "80px" }}>All</th>
                        </tr>
                    </thead>
                    <tbody>
                        {MODULES_CONFIG.map((mod, idx) => {
                            if (mod.type === 'single') {
                                const key = mod.name + ':null';
                                const p = permissions[key] || {};
                                const allChecked = p.view && p.add && p.edit && p.delete;
                                return (
                                    <tr key={key} style={{ borderBottom: "1px solid #eee", backgroundColor: idx % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                                        <td style={{ padding: "8px 15px", fontWeight: "600", color: "#333" }}>
                                            <span style={{ color: '#164E62', marginRight: '8px', fontSize: '10px' }}>●</span>
                                            {mod.name}
                                        </td>
                                        {ACTIONS.map(action => (
                                            <td key={action} style={{ textAlign: "center" }}>
                                                <Checkbox size="small" checked={!!p[action]} onChange={() => handlePermChange(key, action)}
                                                    sx={{ '&.Mui-checked': { color: '#164E62' } }} />
                                            </td>
                                        ))}
                                        <td style={{ textAlign: "center" }}>
                                            <Checkbox size="small" checked={!!allChecked} onChange={() => handleSelectAllForKey(key)}
                                                sx={{ '&.Mui-checked': { color: '#e57373' } }} />
                                        </td>
                                    </tr>
                                );
                            }

                            // Group item
                            const isExpanded = expandedGroups[mod.name];
                            const groupAllChecked = isGroupAllChecked(mod);

                            return (
                                <React.Fragment key={mod.name}>
                                    {/* Group header row */}
                                    <tr style={{ borderBottom: "1px solid #ccc", backgroundColor: "#EDF6F9", cursor: "pointer" }}
                                        onClick={() => toggleGroup(mod.name)}>
                                        <td style={{ padding: "10px 15px", fontWeight: "700", color: "#164E62" }}>
                                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ fontSize: "12px", width: "14px" }}>{isExpanded ? '▼' : '▶'}</span>
                                                <span>{mod.name}</span>
                                                <span style={{ fontSize: "11px", color: "#888", fontWeight: "400" }}>({mod.subModules.length} items)</span>
                                            </span>
                                        </td>
                                        {ACTIONS.map(action => (
                                            <td key={action} style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                                <Checkbox size="small"
                                                    checked={isGroupActionChecked(mod, action)}
                                                    indeterminate={isGroupPartial(mod, action)}
                                                    onChange={() => handleGroupToggle(mod, action)}
                                                    sx={{ '&.Mui-checked': { color: '#164E62' }, '&.MuiCheckbox-indeterminate': { color: '#90a4ae' } }} />
                                            </td>
                                        ))}
                                        <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                            <Checkbox size="small" checked={groupAllChecked} onChange={() => handleGroupSelectAll(mod)}
                                                sx={{ '&.Mui-checked': { color: '#e57373' } }} />
                                        </td>
                                    </tr>

                                    {/* Sub-module rows (shown when expanded) */}
                                    {isExpanded && mod.subModules.map((sub, subIdx) => {
                                        const key = mod.name + ':' + sub;
                                        const p = permissions[key] || {};
                                        const allChecked = p.view && p.add && p.edit && p.delete;
                                        return (
                                            <tr key={key} style={{ borderBottom: "1px solid #f0f0f0", backgroundColor: subIdx % 2 === 0 ? '#fafcfd' : '#fff' }}>
                                                <td style={{ padding: "6px 15px 6px 45px", color: "#555", fontSize: "13px" }}>
                                                    <span style={{ color: '#b0b0b0', marginRight: '6px' }}>{subIdx === mod.subModules.length - 1 ? '└' : '├'}</span>
                                                    {sub}
                                                </td>
                                                {ACTIONS.map(action => (
                                                    <td key={action} style={{ textAlign: "center" }}>
                                                        <Checkbox size="small" checked={!!p[action]} onChange={() => handlePermChange(key, action)}
                                                            sx={{ '&.Mui-checked': { color: '#1a7a8a' }, padding: '2px' }} />
                                                    </td>
                                                ))}
                                                <td style={{ textAlign: "center" }}>
                                                    <Checkbox size="small" checked={!!allChecked} onChange={() => handleSelectAllForKey(key)}
                                                        sx={{ '&.Mui-checked': { color: '#e57373' }, padding: '2px' }} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Submit buttons */}
            <div style={{ marginTop: "25px", display: "flex", gap: "15px" }}>
                <div onClick={handleSubmit} style={{ fontWeight: "500", backgroundColor: "#164E62", color: "#fff", padding: "10px 30px", borderRadius: "5px", cursor: "pointer", fontSize: "15px" }}>
                    {isEdit ? 'Update Role' : 'Create Role'}
                </div>
                <div onClick={() => navigate("/roles")} style={{ fontWeight: "500", backgroundColor: "#EDF2F5", color: "#000", padding: "10px 30px", borderRadius: "5px", cursor: "pointer", fontSize: "15px" }}>
                    Cancel
                </div>
            </div>
        </div>
    );
};

export default AddRole;
